import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TeklifForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [musteriler, setMusteriler] = useState([]);
  const [bankaHesaplari, setBankaHesaplari] = useState([]);
  const [formData, setFormData] = useState({
    musteri_id: '',
    teklif_no: '',
    tarih: new Date().toISOString().split('T')[0],
    gecerlilik: 30,
    para_birimi: 'TL',
    kdv_orani: 20,
    banka_hesap_id: '',
    kategoriler: [],
    teslimat_suresi: '',
    nakliye: 'Dahil',
    mobil_vinc: 'Dahil',
    kalemler: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    if (isEdit) {
      fetchTeklif();
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchData = async () => {
    try {
      const [musterilerRes, bankaRes] = await Promise.all([
        axios.get(`${API}/musteriler`),
        axios.get(`${API}/banka-hesaplari`)
      ]);
      setMusteriler(musterilerRes.data);
      setBankaHesaplari(bankaRes.data);
    } catch (error) {
      console.error('Veriler yüklenirken hata:', error);
    }
  };

  const fetchTeklif = async () => {
    try {
      const response = await axios.get(`${API}/teklifler/${id}`);
      const data = response.data;
      data.tarih = new Date(data.tarih).toISOString().split('T')[0];
      setFormData(data);
    } catch (error) {
      console.error('Teklif yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      submitData.tarih = submitData.tarih + 'T12:00:00.000Z';
      
      if (isEdit) {
        await axios.put(`${API}/teklifler/${id}`, submitData);
      } else {
        await axios.post(`${API}/teklifler`, submitData);
      }
      navigate('/teklifler');
    } catch (error) {
      console.error('Teklif kaydedilirken hata:', error);
      alert('Teklif kaydedilemedi');
    }
  };

  const handleChange = (e) => {
    const value = e.target.type === 'number' ? parseFloat(e.target.value) : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      kategoriler: checked
        ? [...prev.kategoriler, value]
        : prev.kategoriler.filter(k => k !== value)
    }));
  };

  const addKalem = () => {
    setFormData({
      ...formData,
      kalemler: [...formData.kalemler, { aciklama: '', miktar: 1, birim_fiyat: 0 }]
    });
  };

  const removeKalem = (index) => {
    const newKalemler = formData.kalemler.filter((_, i) => i !== index);
    setFormData({ ...formData, kalemler: newKalemler });
  };

  const updateKalem = (index, field, value) => {
    const newKalemler = [...formData.kalemler];
    newKalemler[index][field] = field === 'aciklama' ? value : parseFloat(value) || 0;
    setFormData({ ...formData, kalemler: newKalemler });
  };

  const calculateSubtotal = () => {
    return formData.kalemler.reduce((sum, item) => sum + (item.miktar * item.birim_fiyat), 0);
  };

  const calculateKdv = () => {
    return calculateSubtotal() * (formData.kdv_orani / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateKdv();
  };

  if (loading) {
    return <div className="loading" data-testid="loading">Yükleniyor...</div>;
  }

  return (
    <div data-testid="proposal-form-page">
      <div className="page-header">
        <h1 className="page-title">{isEdit ? 'Teklif Görüntüle' : 'Yeni Teklif'}</h1>
        <p className="page-subtitle">Teklif bilgilerini giriniz</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Temel Bilgiler</h3>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Müşteri *</label>
              <select
                name="musteri_id"
                className="form-select"
                value={formData.musteri_id}
                onChange={handleChange}
                required
                data-testid="proposal-customer"
              >
                <option value="">Müşteri seçiniz</option>
                {musteriler.map(m => (
                  <option key={m.id} value={m.id}>{m.ad}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Teklif No *</label>
              <input
                type="text"
                name="teklif_no"
                className="form-input"
                value={formData.teklif_no}
                onChange={handleChange}
                required
                data-testid="proposal-number"
              />
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Tarih *</label>
              <input
                type="date"
                name="tarih"
                className="form-input"
                value={formData.tarih}
                onChange={handleChange}
                required
                data-testid="proposal-date"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Geçerlilik (Gün)</label>
              <input
                type="number"
                name="gecerlilik"
                className="form-input"
                value={formData.gecerlilik}
                onChange={handleChange}
                data-testid="proposal-validity"
              />
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Para Birimi</label>
              <select
                name="para_birimi"
                className="form-select"
                value={formData.para_birimi}
                onChange={handleChange}
                data-testid="proposal-currency"
              >
                <option value="TL">TL</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">KDV Oranı (%)</label>
              <input
                type="number"
                name="kdv_orani"
                className="form-input"
                value={formData.kdv_orani}
                onChange={handleChange}
                step="0.01"
                data-testid="proposal-vat"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Banka Hesabı</label>
            <select
              name="banka_hesap_id"
              className="form-select"
              value={formData.banka_hesap_id}
              onChange={handleChange}
              data-testid="proposal-bank"
            >
              <option value="">Banka hesabı seçiniz</option>
              {bankaHesaplari.map(b => (
                <option key={b.id} value={b.id}>{b.kisa_ad} - {b.banka_adi}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Kategoriler</label>
            <div style={{ display: 'flex', gap: '24px', marginTop: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  value="Yedek Parça"
                  checked={formData.kategoriler.includes('Yedek Parça')}
                  onChange={handleCheckboxChange}
                />
                Yedek Parça
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  value="Montaj"
                  checked={formData.kategoriler.includes('Montaj')}
                  onChange={handleCheckboxChange}
                />
                Montaj
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  value="Bakım"
                  checked={formData.kategoriler.includes('Bakım')}
                  onChange={handleCheckboxChange}
                />
                Bakım
              </label>
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Teslimat Süresi</label>
              <input
                type="text"
                name="teslimat_suresi"
                className="form-input"
                value={formData.teslimat_suresi}
                onChange={handleChange}
                placeholder="örn: 45 gün"
                data-testid="proposal-delivery"
              />
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Nakliye</label>
              <select
                name="nakliye"
                className="form-select"
                value={formData.nakliye}
                onChange={handleChange}
                data-testid="proposal-transport"
              >
                <option value="Dahil">Dahil</option>
                <option value="Hariç">Hariç</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Mobil Vinç</label>
              <select
                name="mobil_vinc"
                className="form-select"
                value={formData.mobil_vinc}
                onChange={handleChange}
                data-testid="proposal-mobile-crane"
              >
                <option value="Dahil">Dahil</option>
                <option value="Hariç">Hariç</option>
              </select>
            </div>
          </div>
        </div>

        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0 }}>Teklif Kalemleri</h3>
            <button type="button" className="btn btn-secondary" onClick={addKalem} data-testid="add-item-btn">
              + Kalem Ekle
            </button>
          </div>
          {formData.kalemler.map((kalem, index) => (
            <div key={index} style={{ display: 'grid', gridTemplateColumns: '2fr 100px 150px 150px 60px', gap: '12px', marginBottom: '12px' }} data-testid={`item-${index}`}>
              <input
                type="text"
                className="form-input"
                placeholder="Açıklama"
                value={kalem.aciklama}
                onChange={(e) => updateKalem(index, 'aciklama', e.target.value)}
              />
              <input
                type="number"
                className="form-input"
                placeholder="Miktar"
                value={kalem.miktar}
                onChange={(e) => updateKalem(index, 'miktar', e.target.value)}
                step="0.01"
              />
              <input
                type="number"
                className="form-input"
                placeholder="Birim Fiyat"
                value={kalem.birim_fiyat}
                onChange={(e) => updateKalem(index, 'birim_fiyat', e.target.value)}
                step="0.01"
              />
              <div style={{ padding: '10px 12px', background: '#f8fafc', borderRadius: '8px', fontWeight: 600 }}>
                {(kalem.miktar * kalem.birim_fiyat).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
              </div>
              <button type="button" className="btn btn-danger" onClick={() => removeKalem(index)} style={{ padding: '8px' }}>
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}

          <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '2px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '80px', fontSize: '14px' }}>
              <div>
                <div style={{ marginBottom: '8px' }}>
                  <strong>Ara Toplam:</strong>
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <strong>KDV ({formData.kdv_orani}%):</strong>
                </div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
                  <strong>GENEL TOPLAM:</strong>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ marginBottom: '8px' }}>
                  {calculateSubtotal().toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {formData.para_birimi}
                </div>
                <div style={{ marginBottom: '8px' }}>
                  {calculateKdv().toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {formData.para_birimi}
                </div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
                  {calculateTotal().toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {formData.para_birimi}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" data-testid="submit-btn">
            {isEdit ? 'Güncelle' : 'Kaydet'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/teklifler')} data-testid="cancel-btn">
            İptal
          </button>
        </div>
      </form>
    </div>
  );
};

export default TeklifForm;